import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { formatters } from 'services';
import type { AFSDV01HistoryPoint } from 'state';
import type { RelativizeDate } from '../helpers';

type Props = {
  history: AFSDV01HistoryPoint[];
  netDv01Limit: number | null;
  grossDv01Limit: number | null;
  currencySymbol: string;
  /** Trainee historical view: replace every calendar date with "Day N". */
  relativizeDate?: RelativizeDate;
  height?: number | string;
};

type Point = { x: number; y: number };
export type Dv01Series = { name: string; data: Point[] };

const formatScenarioDate = (value: number): string =>
  new Date(value).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

export const formatDv01Date = (
  value: number,
  relativizeDate?: RelativizeDate,
): string => {
  if (!Number.isFinite(value)) return relativizeDate ? 'Day —' : '';
  if (relativizeDate) {
    // A trainee-safe formatter must never fall back to the calendar date.
    return relativizeDate(new Date(value).toISOString()) ?? 'Day —';
  }
  return formatScenarioDate(value);
};

export const formatDv01Value = (
  value: number,
  currencySymbol: string,
): string =>
  `${formatters.commas2Digits(value)}${
    currencySymbol ? ` ${currencySymbol}` : ''
  }`;

export const buildDv01Series = (
  history: AFSDV01HistoryPoint[],
  netDv01Limit: number | null,
  grossDv01Limit: number | null,
): Dv01Series[] => {
  const rows = history
    .map(point => ({
      x: Date.parse(point.scenario_date),
      net: point.net_dv01,
      gross: point.gross_dv01,
    }))
    .filter(
      point =>
        Number.isFinite(point.x) &&
        Number.isFinite(point.net) &&
        Number.isFinite(point.gross),
    )
    .sort((a, b) => a.x - b.x);

  const series: Dv01Series[] = [
    {
      name: 'Net DV01',
      data: rows.map(point => ({ x: point.x, y: point.net })),
    },
    {
      name: 'Gross DV01',
      data: rows.map(point => ({ x: point.x, y: point.gross })),
    },
  ];
  if (netDv01Limit !== null && Number.isFinite(netDv01Limit)) {
    series.push({
      name: 'Net DV01 limit',
      data: rows.map(point => ({ x: point.x, y: netDv01Limit })),
    });
  }
  if (grossDv01Limit !== null && Number.isFinite(grossDv01Limit)) {
    series.push({
      name: 'Gross DV01 limit',
      data: rows.map(point => ({ x: point.x, y: grossDv01Limit })),
    });
  }
  return series;
};

/** Selected player's persisted per-period AFS portfolio-DV01 evolution. */
const Dv01EvolutionChart: React.FC<Props> = ({
  history,
  netDv01Limit,
  grossDv01Limit,
  currencySymbol,
  relativizeDate,
  height = '100%',
}) => {
  const series = useMemo(
    () => buildDv01Series(history, netDv01Limit, grossDv01Limit),
    [grossDv01Limit, history, netDv01Limit],
  );
  const hasPoints = series[0]?.data.length > 0;

  const options = useMemo<ApexOptions>(() => {
    const isLimit = (name: string) => name.endsWith(' limit');
    const colors: Record<string, string> = {
      'Net DV01': '#0e9f6e',
      'Gross DV01': '#0f5fd6',
      'Net DV01 limit': '#d48806',
      'Gross DV01 limit': '#b42318',
    };
    return {
      chart: {
        type: 'line',
        height,
        parentHeightOffset: 0,
        animations: { enabled: false },
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: series.map(item => colors[item.name]),
      dataLabels: { enabled: false },
      stroke: {
        curve: 'straight',
        width: series.map(item => (isLimit(item.name) ? 2 : 2.5)),
        dashArray: series.map(item => (isLimit(item.name) ? 6 : 0)),
      },
      markers: { size: 0, hover: { size: 4 } },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '11px',
        offsetY: -4,
      },
      grid: { padding: { top: -8, right: 8, bottom: 0, left: 8 } },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: true,
          style: { fontSize: '10px' },
          formatter: (value, timestamp) =>
            formatDv01Date(
              timestamp ?? Date.parse(String(value)),
              relativizeDate,
            ),
        },
        tooltip: { enabled: false },
      },
      yaxis: {
        labels: {
          formatter: value => formatDv01Value(value, currencySymbol),
          style: { fontSize: '10px' },
        },
      },
      tooltip: {
        shared: true,
        x: {
          formatter: value => formatDv01Date(Number(value), relativizeDate),
        },
        y: { formatter: value => formatDv01Value(value, currencySymbol) },
      },
    };
  }, [currencySymbol, height, relativizeDate, series]);

  if (!hasPoints) {
    return (
      <div className="studio2__dv01-chart-empty">No DV01 snapshots yet</div>
    );
  }

  return (
    <div className="studio2__dv01-chart" style={{ height }}>
      <Chart options={options} series={series} type="line" height={height} />
    </div>
  );
};

export default Dv01EvolutionChart;
